import { useAuth } from "../../common/hooks/useAuth";
import "./Login.scss";
import logo from "../../common/assets/gfps-logo.png";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Stack,
  InputGroup,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faFingerprint,
  faSpinner,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import Flag from "react-world-flags";
import { useLang } from "../../common/hooks/useLang";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import i18next from "i18next";
import {
  NAMESPACE_LOCAL,
  NAMESPACE_REMOTE,
} from "../../common/translations/TranslationHelper";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";

export default function Login() {
  const [username, setUsername] = useState();
  const handleUsernameChanged = (value) => {
    setUsername(value);
  };

  const [loginAttempt, setLoginAttempt] = useState(undefined);
  const handleLoginAttemptChanged = (value) => {
    setLoginAttempt(value);
  };

  const [changePasswordAttempt, setChangePasswordAttempt] = useState(undefined);
  const handleChangePasswordAttemptChanged = (value) => {
    setChangePasswordAttempt(value);
  };

  const isLogin = () => {
    return (
      loginAttempt === undefined ||
      loginAttempt == null ||
      loginAttempt.result !== "A"
    );
  };

  const isChangePassword = () => {
    return (
      loginAttempt !== undefined &&
      loginAttempt !== null &&
      loginAttempt.result === "A"
    );
  };

  if (isLogin()) {
    return (
      <DefaultLogin
        username={username}
        handleUsernameChanged={handleUsernameChanged}
        loginAttempt={loginAttempt}
        handleLoginAttemptChanged={handleLoginAttemptChanged}
      />
    );
  } else if (isChangePassword()) {
    return (
      <ChangePassword
        username={username}
        changePasswordAttempt={changePasswordAttempt}
        handleChangePasswordAttemptChanged={handleChangePasswordAttemptChanged}
      />
    );
  }
}

export function DefaultLogin(props) {
  const auth = useAuth();
  const { setCurrentLanguage } = useLang();
  const { t } = useTranslation(NAMESPACE_LOCAL);
  const navigate = useNavigate();
  const { state } = useLocation();

  const passwordRef = useRef();

  const [loginInProgress, setLoginInProgress] = useState(false);
  const handleLogin = async (event) => {
    event.preventDefault();

    let loginAttempt = undefined;
    if (props.username !== null && passwordRef.current.value !== "") {
      // Reset messages of last login attempt
      props.handleLoginAttemptChanged(undefined);

      // Show loading indicator
      setLoginInProgress(true);
      loginAttempt = await auth.login(
        props.username,
        passwordRef.current.value,
      );
      setLoginInProgress(false);
    }

    if (loginAttempt != null) {
      if (loginAttempt.result === "A") {
        // Load translations before change password dialog is rendered
        await i18next.reloadResources();
      }
      props.handleLoginAttemptChanged(loginAttempt);
    }
  };

  useEffect(() => {
    async function loginAsync() {
      if (props.loginAttempt?.result === "J") {
        // Async -> Navigate AFTER the translations are loaded (successfully or not)
        await i18next.reloadResources();
        navigate((state && state.path + state.search) || "/home", {
          replace: true,
        });
      }
    }
    loginAsync();
  }, [props.loginAttempt, navigate, state]);

  const [passwordVisible, setPasswordVisible] = useState(false);

  return (
    <Container fluid className="vh-100">
      <Row className="justify-content-center align-items-center h-100">
        <Col
          xs={8}
          sm={7}
          md={6}
          lg={5}
          xl={4}
          className="shadow p-3 rounded-3"
        >
          <Form onSubmit={handleLogin}>
            <Form.Group className="text-center mb-3">
              <img src={logo} height="100" alt="GfPS" title="GfPS" />
            </Form.Group>
            <Form.Group>
              <Form.Text>
                <h1 className="h3 mb-3">{t("KEY_LOGIN_HEADLINE")}</h1>
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <input
                type="text"
                className="form-control"
                id="inputUsername"
                defaultValue={props.username}
                placeholder={t("KEY_USERNAME")}
                autoFocus={true}
                autoComplete="off"
                onChange={(e) => props.handleUsernameChanged(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <InputGroup>
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="form-control pe-5"
                  id="inputPassword"
                  ref={passwordRef}
                  placeholder={t("KEY_PASSWORD")}
                  autoComplete="new-password"
                />
                <Button
                  onClick={(e) => setPasswordVisible(!passwordVisible)}
                  className="bg-transparent border-0 position-absolute end-0"
                  style={{ zIndex: 3 }}
                >
                  <FontAwesomeIcon
                    icon={passwordVisible ? faEye : faEyeSlash}
                  />
                </Button>
              </InputGroup>
            </Form.Group>
            {props.loginAttempt !== undefined &&
              props.loginAttempt.result === "G" && (
                <div
                  className="alert alert-danger d-flex align-items-center"
                  role="alert"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  {t("KEY_WEBUSER_LOCKED")}
                </div>
              )}
            {props.loginAttempt !== undefined &&
              props.loginAttempt.result !== "J" &&
              props.loginAttempt.result !== "G" && (
                <div
                  className="alert alert-danger d-flex align-items-center"
                  role="alert"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  {t("KEY_LOGIN_FAILED")}
                </div>
              )}
            <Button
              className="form-control mb-4"
              variant="primary text-white"
              type="submit"
            >
              <FontAwesomeIcon
                className={loginInProgress ? "fa-spin me-1" : "me-1"}
                icon={loginInProgress ? faSpinner : faFingerprint}
              />
              {t("KEY_LOGIN")}
            </Button>

            <Stack
              direction="horizontal"
              gap={1}
              className="justify-content-center mb-3"
            >
              <Button variant="link" className="m-0 p-0">
                <Flag
                  code="de"
                  width="24"
                  onClick={(e) => setCurrentLanguage("de")}
                />
              </Button>

              <Button variant="link" className="m-0 p-0">
                <Flag
                  code="gb"
                  width="24"
                  onClick={(e) => setCurrentLanguage("en")}
                />
              </Button>
            </Stack>

            <Form.Text className="text-muted">
              {t("KEY_LOGIN_HINT_1")}
              <br />
              {t("KEY_LOGIN_HINT_2")}&nbsp;
              <a href="mailto:customersupport@gfps.de">
                customersupport@gfps.de
              </a>
            </Form.Text>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

export function ChangePassword(props) {
  const auth = useAuth();
  const { setCurrentLanguage } = useLang();
  const { t } = useTranslation(NAMESPACE_REMOTE);
  const navigate = useNavigate();
  const { state } = useLocation();

  const passwordRef = useRef();
  const passwordNewRef = useRef();
  const passwordNewRepeatRef = useRef();

  const [loginInProgress, setLoginInProgress] = useState(false);
  const handleChangePassword = async (event) => {
    event.preventDefault();

    // Show loading indicator
    setLoginInProgress(true);
    const changePasswordAttempt = await auth.changePassword(
      props.username,
      passwordRef.current.value,
      passwordNewRef.current.value,
      passwordNewRepeatRef.current.value,
    );
    setLoginInProgress(false);
    props.handleChangePasswordAttemptChanged(changePasswordAttempt);
  };

  useEffect(() => {
    passwordRef.current.focus();
    async function loginAsync() {
      if (props.changePasswordAttempt?.result === "J") {
        // Async -> Navigate AFTER the translations are loaded (successfully or not)
        await i18next.reloadResources();
        navigate((state && state.path + state.search) || "/home", {
          replace: true,
        });
      }
    }
    loginAsync();
  }, [props.changePasswordAttempt, navigate, state]);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordNewVisible, setPasswordNewVisible] = useState(false);
  const [passwordNewRepeatVisible, setPasswordNewRepeatVisible] =
    useState(false);

  return (
    <Container fluid className="vh-100">
      <Row className="justify-content-center align-items-center h-100">
        <Col
          xs={8}
          sm={7}
          md={6}
          lg={5}
          xl={4}
          className="shadow p-3 rounded-3"
        >
          <Form onSubmit={handleChangePassword}>
            <Form.Group className="text-center mb-3">
              <img src={logo} height="100" alt="GfPS" title="GfPS" />
            </Form.Group>
            <Form.Group>
              <Form.Text>
                <h1 className="h3 mb-3">
                  {t("PROMPT.WSHOP2.CHANGE_PASSWORD_DIALOG.TITLE")}
                </h1>
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <InputGroup>
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="form-control pe-5"
                  id="inputPassword"
                  ref={passwordRef}
                  placeholder={t(
                    "PROMPT.WSHOP2.CHANGE_PASSWORD_DIALOG.OLD_PASSWORD",
                  )}
                  autoComplete="new-password"
                />
                <Button
                  onClick={(e) => setPasswordVisible(!passwordVisible)}
                  className="bg-transparent border-0 position-absolute end-0"
                  style={{ zIndex: 3 }}
                >
                  <FontAwesomeIcon
                    icon={passwordVisible ? faEye : faEyeSlash}
                  />
                </Button>
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <InputGroup>
                <input
                  type={passwordNewVisible ? "text" : "password"}
                  className="form-control pe-5"
                  id="inputPasswordNew"
                  ref={passwordNewRef}
                  placeholder={t(
                    "PROMPT.WSHOP2.CHANGE_PASSWORD_DIALOG.NEW_PASSWORD",
                  )}
                  autoComplete="new-password"
                />
                <Button
                  onClick={(e) => setPasswordNewVisible(!passwordNewVisible)}
                  className="bg-transparent border-0 position-absolute end-0"
                  style={{ zIndex: 3 }}
                >
                  <FontAwesomeIcon
                    icon={passwordNewVisible ? faEye : faEyeSlash}
                  />
                </Button>
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <InputGroup>
                <input
                  type={passwordNewRepeatVisible ? "text" : "password"}
                  className="form-control pe-5"
                  id="inputPasswordNewRepeat"
                  ref={passwordNewRepeatRef}
                  placeholder={t(
                    "PROMPT.WSHOP2.CHANGE_PASSWORD_DIALOG.NEW_PASSWORD_REPEAT",
                  )}
                  autoComplete="new-password"
                />
                <Button
                  onClick={(e) =>
                    setPasswordNewRepeatVisible(!passwordNewRepeatVisible)
                  }
                  className="bg-transparent border-0 position-absolute end-0"
                  style={{ zIndex: 3 }}
                >
                  <FontAwesomeIcon
                    icon={passwordNewRepeatVisible ? faEye : faEyeSlash}
                  />
                </Button>
              </InputGroup>
            </Form.Group>
            {props.changePasswordAttempt === undefined && (
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="me-2"
                />
                {t("TEXT.WSHOP2.CHANGE_PASSWORD_DIALOG.CHANGE_PASSWORD_HINT")}
              </div>
            )}
            {props.changePasswordAttempt !== undefined &&
              props.changePasswordAttempt.result !== "J" && (
                <div
                  className="alert alert-danger d-flex align-items-center"
                  role="alert"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  {t(
                    "TEXT.WSHOP2.CHANGE_PASSWORD_DIALOG.CHANGE_PASSWORD_FAILED",
                  )}
                </div>
              )}
            <Button
              className="form-control mb-4"
              variant="primary text-white"
              type="submit"
            >
              <FontAwesomeIcon
                className={loginInProgress ? "fa-spin me-1" : "me-1"}
                icon={loginInProgress ? faSpinner : faFingerprint}
              />
              {t(
                "PROMPT.WSHOP2.CHANGE_PASSWORD_DIALOG.CHANGE_PASSWORD_CONFIRM",
              )}
            </Button>

            <Stack
              direction="horizontal"
              gap={1}
              className="justify-content-center mb-3"
            >
              <Button variant="link" className="m-0 p-0">
                <Flag
                  code="de"
                  width="24"
                  onClick={(e) => setCurrentLanguage("de")}
                />
              </Button>

              <Button variant="link" className="m-0 p-0">
                <Flag
                  code="gb"
                  width="24"
                  onClick={(e) => setCurrentLanguage("en")}
                />
              </Button>
            </Stack>

            <Form.Text className="text-muted">
              {t("TEXT.WSHOP2.CHANGE_PASSWORD_DIALOG.PASSWORD_RULES_HINT")}
            </Form.Text>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}
